import {
  Box,
  Button,
  Flex,
  Pagination,
  Select,
  Table,
  Typography,
  useClientPagination,
} from "@qws-org/ui-kit";
import type { FC } from "react";
import { useState } from "react";

import type {
  LeaderboardLimit,
  LeaderboardRound,
  TransformerLeaderboard,
} from "~/core";
import { Medal } from "~/icons";

type LeaderBoardTableProps = {
  data: TransformerLeaderboard;
};

export const LeaderBoardTable: FC<LeaderBoardTableProps> = ({ data }) => {
  const rounds = Object.keys(data);
  const limits = Object.keys(data[rounds?.[0]]);
  const [round, setRound] = useState<LeaderboardRound>(rounds?.[0]);
  const [limit, setLimit] = useState<LeaderboardLimit>(limits?.[0]);
  const spreadsheetData = data[round][limit] || [];

  const { paginatedData, paginationProps } = useClientPagination({
    data: spreadsheetData,
  });

  const disableLimit = round === "Total score" ? "Random" : "";
  const disableRound = limit === "Random" ? "Total score" : "";

  const handleRoundSelect = (selectedRound: LeaderboardRound): void => {
    setRound(selectedRound);
    paginationProps.onPageChange(0);
  };

  const handleLimitSelect = (selectedLimit: LeaderboardLimit): void => {
    setLimit(selectedLimit);
    paginationProps.onPageChange(0);
  };

  return (
    <>
      <Flex
        align="center"
        justify="center"
        gap={4}
        m={{ mb: 24 }}
        display="hidden"
        md={{ display: "flex", m: { mb: 24 } }}
      >
        {rounds.map((r) => (
          <Button
            key={r}
            variant="secondary"
            size="small"
            fontSize="button.mobile.xsmall"
            isActive={round === r}
            md={{ p: { px: 24, py: 8 } }}
            onPress={() => handleRoundSelect(r)}
            isDisabled={r === disableRound}
            data-testid={`leader-board-round-select-button-${r}`}
          >
            {r}
          </Button>
        ))}
      </Flex>
      <Flex
        align="center"
        justify="center"
        gap={4}
        m={{ mb: 40 }}
        display="hidden"
        md={{ display: "flex", m: { mb: 40 } }}
        d={{ display: "flex", m: { mb: 40 } }}
      >
        {limits.map((currentLimit) => (
          <Button
            key={currentLimit}
            variant="secondary"
            size="small"
            fontSize="button.mobile.xsmall"
            md={{ p: { px: 24, py: 8 } }}
            p={{ px: 24, py: 8 }}
            isActive={limit === currentLimit}
            onPress={() => handleLimitSelect(currentLimit)}
            isDisabled={currentLimit === disableLimit}
            data-testid={`leader-board-limit-select-button-${currentLimit}`}
          >
            {currentLimit}
          </Button>
        ))}
      </Flex>
      <Flex
        align="center"
        justify="center"
        gap={4}
        m={{ mb: 16 }}
        display="flex"
        md={{ display: "hidden" }}
      >
        <Select
          minWidth={162}
          value={round}
          onChange={setRound}
          items={rounds}
          data-testid="leader-board-round-select"
        />

        <Select
          minWidth={162}
          value={limit}
          onChange={setLimit}
          items={limits}
          data-testid="leader-board-limit-select"
        />
      </Flex>

      <Box md={{ maxWidth: 700 }} width="100%" minWidth={312}>
        <Table
          m={{ mx: "auto" }}
          aria-label="LeaderBoard"
          data-testid="leader-board-table"
        >
          <Table.Header>
            <Table.Column> </Table.Column>
            <Table.Column>
              <Typography
                fontWeight="mobile.normal"
                size="small"
                m={0}
                p={{ py: 12, px: 8 }}
                md={{
                  fontWeight: "desktop.semibold",
                  fontSize: "body.desktop.small",
                  lineHeight: "body.desktop.large",
                  p: { py: 0, px: 0 },
                }}
                horizontalAlign="left"
                color="text-secondary-default"
              >
                Nickname
              </Typography>
            </Table.Column>
            <Table.Column>
              <Typography
                fontWeight="mobile.normal"
                size="small"
                md={{
                  fontWeight: "desktop.semibold",
                  fontSize: "body.desktop.small",
                  lineHeight: "body.desktop.large",
                  p: { py: 0, px: 0 },
                }}
                p={{ py: 12, px: 8 }}
                horizontalAlign="right"
                color="text-secondary-default"
                maxWidth={200}
                minWidth={80}
              >
                Score
              </Typography>
            </Table.Column>
            <Table.Column>
              <Typography
                fontWeight="mobile.normal"
                size="small"
                md={{
                  fontWeight: "desktop.semibold",
                  fontSize: "body.desktop.small",
                  lineHeight: "body.desktop.large",
                  p: { py: 0, px: 0 },
                }}
                p={{ py: 12, px: 8 }}
                horizontalAlign="right"
                color="text-secondary-default"
                maxWidth={200}
                minWidth={80}
              >
                Reward
              </Typography>
            </Table.Column>
          </Table.Header>
          <Table.Body>
            {paginatedData.map((item) => (
              <Table.Row key={item.id}>
                <Table.Cell>
                  {Number(item.place) <= 5 ? (
                    <Flex md={{ gap: 17 }} align="center" justify={"flex-end"}>
                      <Medal
                        className="hidden md:block"
                        width="24"
                        height="24"
                        viewBox="0 0 26 26"
                      />
                      <Typography
                        color="text-accent-default"
                        size="small"
                        md={{
                          size: "medium",
                          weight: "semibold",
                        }}
                        p={{ pr: 8 }}
                      >
                        {item.place}
                      </Typography>
                    </Flex>
                  ) : (
                    <Typography
                      maxHeight={24}
                      size="small"
                      md={{
                        size: "medium",
                        weight: "semibold",
                      }}
                      horizontalAlign="right"
                      p={{ pr: 8 }}
                    >
                      {item.place}
                    </Typography>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <Typography size="small" md={{ size: "medium" }}>
                    {item.nick}
                  </Typography>
                </Table.Cell>
                <Table.Cell>
                  <Typography
                    size="small"
                    md={{ size: "medium" }}
                    horizontalAlign="right"
                  >
                    {item.points}
                  </Typography>
                </Table.Cell>
                <Table.Cell>
                  <Typography
                    color={`${Number(item.place) <= 5 ? "text-accent-default" : ""}`}
                    size="small"
                    md={{ size: "medium" }}
                    horizontalAlign="right"
                  >
                    {item.reward}
                  </Typography>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Box>

      <Pagination {...paginationProps} />
    </>
  );
};
